import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Home, 
  Calendar, 
  MessageSquare, 
  Plus,
  ArrowRight,
  Sparkles,
  Wrench,
  Leaf,
  Brush,
  CreditCard,
  Package
} from 'lucide-react';
import { format } from 'date-fns';
import { getFullImageUrl } from '../lib/imageUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const serviceIcons = {
  cleaning: Brush,
  garden: Leaf,
  repairs: Wrench,
  maintenance: Wrench,
  rental_prep: Sparkles,
};

const DashboardPage = () => {
  const { user, language, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const [properties, setProperties] = useState([]);
  const [services, setServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [myPackages, setMyPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const HOURLY_RATES = {
    cleaning: 18, garden: 25, repairs: 25, maintenance: 25, rental_prep: 20, urgent: 35
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, servicesRes, packagesRes] = await Promise.all([
        axios.get(`${API_URL}/properties`),
        axios.get(`${API_URL}/services`),
        isAdmin ? Promise.resolve({ data: [] }) : axios.get(`${API_URL}/packages/my`).catch(() => ({ data: [] }))
      ]);
      setProperties(propertiesRes.data);
      setAllServices(servicesRes.data);
      setServices(servicesRes.data.slice(0, 5));
      setMyPackages(packagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalRemainingHours = myPackages.reduce((sum, pkg) => sum + pkg.hours_remaining, 0);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-info',
      in_progress: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error',
    };
    return badges[status] || 'badge-info';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            {t('welcome')}, {user?.name}!
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Voici un apercu de vos proprietes et services'
              : 'Here\'s an overview of your properties and services'
            }
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/properties">
            <Button className="btn-secondary" data-testid="add-property-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('addProperty')}
            </Button>
          </Link>
          {!isAdmin && (
            <Link to="/services">
              <Button className="btn-primary" data-testid="book-service-btn">
                <Calendar className="h-4 w-4 mr-2" />
                {t('bookService')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Credits Card - only for owners */}
        {!isAdmin && (
        <Card className="card-base bg-primary/5 border-primary/20" data-testid="stat-credits">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary" data-testid="credits-value">{totalRemainingHours}</p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Crédits (heures)' : 'Credits (hours)'}
                </p>
              </div>
            </div>
            {totalRemainingHours === 0 && (
              <Link to="/services">
                <Button size="sm" className="w-full mt-3 btn-primary" data-testid="buy-credits-btn">
                  <Package className="h-3 w-3 mr-1" />
                  {language === 'fr' ? 'Acheter un forfait' : 'Buy a package'}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
        )}

        <Card className="card-base" data-testid="stat-properties">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Home className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{properties.length}</p>
                <p className="text-sm text-muted-foreground">{t('properties')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-pending">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {services.filter(s => s.status === 'pending').length}
                </p>
                <p className="text-sm text-muted-foreground">{t('pending')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-in-progress">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {services.filter(s => s.status === 'in_progress').length}
                </p>
                <p className="text-sm text-muted-foreground">{t('in_progress')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-completed">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {services.filter(s => s.status === 'completed').length}
                </p>
                <p className="text-sm text-muted-foreground">{t('completed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Charts */}
      {isAdmin && allServices.length > 0 && (() => {
        const statusData = ['pending', 'completed', 'cancelled'].map(status => {
          const filtered = allServices.filter(s => s.status === status);
          const revenue = filtered.reduce((sum, s) => sum + (s.hours_requested || 0) * (HOURLY_RATES[s.service_type] || 25), 0);
          const labels = { pending: language === 'fr' ? 'En attente' : 'Pending', completed: language === 'fr' ? 'Terminé' : 'Completed', cancelled: language === 'fr' ? 'Annulé' : 'Cancelled' };
          const colors = { pending: '#f97316', completed: '#22c55e', cancelled: '#ef4444' };
          return { name: labels[status], count: filtered.length, revenue, fill: colors[status] };
        });
        return (
          <Card className="card-base" data-testid="chart-status">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg font-semibold">
                {language === 'fr' ? 'Services par Statut' : 'Services by Status'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={statusData} margin={{ top: 30, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 14, fontWeight: 600 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip content={({ active, payload }) => {
                    if (active && payload?.[0]) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-semibold">{d.name}</p>
                          <p>{language === 'fr' ? 'Nombre' : 'Count'}: {d.count}</p>
                          <p>{language === 'fr' ? 'Revenu' : 'Revenue'}: {d.revenue}€</p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={80}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-8 mt-3">
                {statusData.map((d, i) => (
                  <div key={i} className="text-center">
                    <span className="inline-block w-3 h-3 rounded mr-1.5" style={{ backgroundColor: d.fill }} />
                    <span className="text-sm font-medium">{d.name}: {d.count}</span>
                    <span className="text-sm text-muted-foreground ml-1">({d.revenue}€)</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Properties section */}
        <div className="lg:col-span-7">
          <Card className="card-base h-full" data-testid="properties-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg font-semibold">{t('yourProperties')}</CardTitle>
              <Link to="/properties">
                <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-properties">
                  {t('viewAll')} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {properties.length === 0 ? (
                <div className="text-center py-12">
                  <Home className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{t('noProperties')}</p>
                  <Link to="/properties">
                    <Button className="btn-primary mt-4" data-testid="add-first-property">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('addProperty')}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {properties.slice(0, 4).map((property, index) => (
                    <div 
                      key={property.id} 
                      className="group rounded-lg border border-border/50 overflow-hidden hover:shadow-md transition-shadow"
                      style={{ animationDelay: `${index * 100}ms` }}
                      data-testid={`property-card-${property.id}`}
                    >
                      <div className="aspect-[16/10] relative overflow-hidden">
                        <img
                          src={property.images && property.images.length > 0 
                            ? getFullImageUrl(property.images[0]) 
                            : (property.image_url 
                              ? getFullImageUrl(property.image_url)
                              : 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')}
                          alt={property.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <span className={`absolute top-2 right-2 ${getStatusBadge(property.status)}`}>
                          {t(property.status)}
                        </span>
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-foreground truncate">{property.name}</h3>
                        <p className="text-sm text-muted-foreground truncate">{property.address}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{property.bedrooms} {t('bedrooms')}</span>
                          <span>{property.bathrooms} {t('bathrooms')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Services section */}
        <div className="lg:col-span-5">
          <Card className="card-base h-full" data-testid="services-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg font-semibold">{t('upcomingServices')}</CardTitle>
              <Link to="/services">
                <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-services">
                  {t('viewAll')} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{t('noServices')}</p>
                  <Link to="/services">
                    <Button className="btn-primary mt-4" data-testid="book-first-service">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('bookService')}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {services.map((service, index) => {
                    const ServiceIcon = serviceIcons[service.service_type] || Calendar;
                    const property = properties.find(p => p.id === service.property_id);
                    return (
                      <div 
                        key={service.id}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-accent/50 transition-colors"
                        style={{ animationDelay: `${index * 100}ms` }}
                        data-testid={`service-item-${service.id}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <ServiceIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">
                            {t(service.service_type)}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {property?.name || 'Property'} • {format(new Date(service.scheduled_date), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <span className={`shrink-0 ${getStatusBadge(service.status)}`}>
                          {t(service.status)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick actions */}
      <Card className="card-base" data-testid="quick-actions-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg font-semibold">{t('quickActions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link to="/services?type=cleaning" className="block">
              <div className="p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors text-center group" data-testid="quick-cleaning">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Brush className="h-6 w-6 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('cleaning')}</span>
              </div>
            </Link>
            <Link to="/services?type=garden" className="block">
              <div className="p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors text-center group" data-testid="quick-garden">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-green-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Leaf className="h-6 w-6 text-green-600" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('garden')}</span>
              </div>
            </Link>
            <Link to="/services?type=repairs" className="block">
              <div className="p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors text-center group" data-testid="quick-repairs">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Wrench className="h-6 w-6 text-orange-600" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('repairs')}</span>
              </div>
            </Link>
            <Link to="/services?type=rental_prep" className="block">
              <div className="p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors text-center group" data-testid="quick-rental">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('rental_prep')}</span>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
